
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Asterisk, Mail } from "lucide-react";
import { TextField, InputAdornment, CircularProgress } from "@mui/material";
import { store } from "@/service/store";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useSignalEffect } from "@preact/signals-react";
import { useSignals } from "@preact/signals-react/runtime";

const Login = () => {
  useSignals();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const isLoading = store.technician.isLoading.value;
  const { t } = useTranslation();

  // With useSignalEffect, the component will automatically re-render when signals change
  useSignalEffect(() => {
    // Check if user is already logged in
    if (store.technician.data.value) {
      toast.success(t('messages.loginSuccess'));
      navigate('/');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error(t('messages.fillAllFields'));
      return;
    }

    // The login method will update the isLoading signal internally
    store.technician.login({ email, password }).catch((error) => {
      console.error("Login error:", error);
      toast.error(t('messages.loginFailed'));
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary/50 p-4 relative">
      <div className="absolute top-16 left-1/2 transform -translate-x-1/2 z-10">
        <img
          src="/lovable-uploads/logo.svg"
          alt="Service Point Logo"
          className="w-32 h-32 mx-auto"
        />
      </div>
      <Card className="w-full max-w-md mt-6">
        <CardHeader className="space-y-1 pt-8">
          <CardTitle className="text-2xl font-bold text-center">
            Service Point
          </CardTitle>
          <CardDescription className="text-center">
            Bitte melden Sie sich mit Ihren Zugangsdaten an
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <TextField
              id="email"
              label="E-Mail"
              type="email"
              variant="outlined"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Mail className="h-5 w-5 text-muted-foreground" />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              id="password"
              label="Passwort"
              type="password"
              variant="outlined"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Asterisk className="h-5 w-5 text-muted-foreground" />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('common.login')
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
